import { SearchCriteria } from "../../pojo/SearchCriteria";
import { S25Util } from "../../util/s25-util";
import { HeadCountOperandOptions } from "./s25ql.const";

export namespace HeadCountCriterion {
    export type OperandValue = (typeof HeadCountOperandOptions)[number]["value"];

    export const CapacityOperands: readonly OperandValue[] = ["capacity", "capacity_layout"];

    export function isCapacityOperand(operand: string | undefined): boolean {
        return operand !== undefined && CapacityOperands.includes(operand as OperandValue);
    }

    /** Options for one side: never the other side's exact value; no capacity on both sides. */
    export function operandsForSide(
        operands: readonly { label: string; value: string }[],
        otherSide: string | undefined,
    ) {
        if (otherSide === undefined) {
            return operands;
        }
        return operands.filter((operand) => {
            if (operand.value === otherSide) {
                return false;
            }
            if (isCapacityOperand(otherSide) && isCapacityOperand(operand.value)) {
                return false;
            }
            return true;
        });
    }

    export function ensureDistinctOperands(stepParam: SearchCriteria.StepParam, changedSide: "left" | "right") {
        const left = stepParam.head_count_left;
        const right = stepParam.head_count_right;
        if (left === undefined || right === undefined) {
            return;
        }

        if (isCapacityOperand(left) && isCapacityOperand(right)) {
            if (changedSide === "left") {
                stepParam.head_count_right = "registered";
            } else {
                stepParam.head_count_left = "registered";
            }
            return;
        }

        if (left !== right) {
            return;
        }

        const fallback =
            HeadCountOperandOptions.find(
                (option) =>
                    option.value !== left &&
                    !(isCapacityOperand(left) && isCapacityOperand(option.value)) &&
                    !(isCapacityOperand(right) && isCapacityOperand(option.value)),
            )?.value ?? ("registered" as OperandValue);

        if (changedSide === "left") {
            stepParam.head_count_right = fallback;
        } else {
            stepParam.head_count_left = fallback;
        }
    }

    export function validateStepParam(stepParam: SearchCriteria.StepParam): string | undefined {
        if (!S25Util.isDefined(stepParam.head_count_left) || !S25Util.isDefined(stepParam.head_count_right)) {
            return "Please fill out this field.";
        }
        if (isCapacityOperand(stepParam.head_count_left) && isCapacityOperand(stepParam.head_count_right)) {
            return "Location capacity cannot be compared to itself.";
        }
        if (stepParam.head_count_left === stepParam.head_count_right) {
            return "Choose two different count types.";
        }
        if (stepParam.head_count_ratio_pct !== undefined && !isValidRatioPercent(stepParam.head_count_ratio_pct)) {
            return "Please fill out this field.";
        }
    }

    function isValidRatioPercent(ratioPercent: unknown): boolean {
        if (ratioPercent === null || ratioPercent === "") {
            return false;
        }
        if (typeof ratioPercent === "string" && ratioPercent.trim() === "") {
            return false;
        }
        return Number.isFinite(Number(ratioPercent));
    }
}
